import React, { memo, useMemo, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import PlaceholderWidget from 'containers/components/widgets/components/PlaceholderWidget';

import './styles.scss';

const propTypes = {
    loading: PropTypes.bool,
    onReady: PropTypes.func,
};

const WidgetLayout = memo(
    ({
        children,
        loading,
        className,
        isDynamic,
        setSize,
        type,
        data,
        id,
        dataLessWidget,
        placeholderHeight,
        onClickLayout,
        onReady,
    }) => {
        const domContent = useRef(null);

        const noData =
            (!dataLessWidget && typeof data === 'object' && Object.keys(data)?.length === 0) ||
            data?.length === 0 ||
            !data;

        const classes = useMemo(() => {
            let classes = ['fm-widget-layout'];
            if (className) classes.push(className);
            if (isDynamic) classes.push('fm-widget-layout--dynamic');
            return classes.join(' ');
        }, [className, isDynamic]);

        const content = useMemo(() => {
            if (loading || noData) {
                return (
                    <PlaceholderWidget
                        width={domContent.current?.offsetWidth}
                        height={placeholderHeight || domContent.current?.offsetHeight}
                        type={type}
                    />
                );
            }
            return children;
        }, [children, loading, noData, type, placeholderHeight]);

        useLayoutEffect(() => {
            if (!setSize || loading || noData) return;

            // Use requestAnimationFrame to ensure DOM is fully rendered
            const rafId = requestAnimationFrame(() => {
                if (domContent.current?.offsetHeight) {
                    setSize(
                        {
                            width: domContent.current?.offsetWidth || 0,
                            height: domContent.current?.offsetHeight || 0,
                        },
                        false,
                    );
                }
            });

            return () => cancelAnimationFrame(rafId);
        }, [setSize, type, loading, noData, children]);

        useLayoutEffect(() => {
            if (!onReady || loading || noData) return;

            // Ensure children are painted before running ready hooks
            const rafId = requestAnimationFrame(() => {
                onReady();
            });

            return () => cancelAnimationFrame(rafId);
        }, [onReady, loading, noData]);

        return (
            <div id={id} className={classes} ref={domContent} onClick={onClickLayout}>
                {content}
            </div>
        );
    },
);

WidgetLayout.propTypes = propTypes;

export default WidgetLayout;
